import type React from 'react';

import type { DefaultCreateCasePageProps } from './DefaultCreateCasePage';
import { KnipperCreateCasePage } from './KnipperCreateCasePage';
import { MedbvCreateCasePage } from './MedbvCreateCasePage';

/**
 * Component type for create case pages
 * All create case pages must accept these props
 */
export type CreateCasePageComponent = React.FC<DefaultCreateCasePageProps>;

/**
 * Workflow page registry for create case
 * Maps workflow types to their custom create case page components
 *
 * Usage:
 * 1. Import your custom page component
 * 2. Add mapping: 'workflow_type': YourCustomCreateCaseComponent
 * 3. The router will automatically use this component for that workflow type
 */
const WORKFLOW_PAGES: Record<string, CreateCasePageComponent> = {
  // PQC workflow - Knipper create case page with live session sidebar
  pqc: KnipperCreateCasePage,

  // UnifiedMedBV workflow - create case page with dynamic payor dropdown
  unifiedmedbv: MedbvCreateCasePage,
  // BHO workflow (BV-2171) - human-only fork of unifiedmedbv; reuses the MedBV create case page
  bho: MedbvCreateCasePage,
  // MEDBHOBV workflow (BV-2328) - human-only clone of bho; reuses the MedBV create case page
  medbhobv: MedbvCreateCasePage,
  // PA Status PBM workflow - reuses the MedBV create case page
  pa_status_pbm: MedbvCreateCasePage,
  // PA Status MedBV workflow - reuses the MedBV create case page
  pa_status_medbv: MedbvCreateCasePage,
  // Add your custom workflow pages here
  // Example:
  // 'pbm': PBMCreateCasePage,
};

/**
 * Get the create case page component for a workflow type
 * Returns undefined if no custom page is registered (will fall back to default)
 *
 * @param workflowType - The workflow type to look up
 * @returns The custom page component or undefined
 */
export function getCreatePageForWorkflow(
  workflowType: string | undefined,
): CreateCasePageComponent | undefined {
  if (!workflowType) {
    return undefined;
  }
  return WORKFLOW_PAGES[workflowType];
}
